import { useState } from "react";
import { localDateAt } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { zoneLabel } from "../../lib/time-zone.ts";
import { dayTitle, errorText, timeText } from "./common.ts";
import { CloseIcon } from "./icons.tsx";
import type { GridOpening } from "./openings.ts";

/**
 * Раскрытое окно записи (`docs/booking-design.md`, 3.2). Живёт в том же
 * поповере, что и карточка занятия (`EntryPopover`), и выглядит как она.
 *
 * Две роли, и роль считается по окну, а не по аккаунту (1.3 проекта):
 *
 * - **своё окно**: одна кнопка «Закрыть запись». Занятого окна здесь не бывает,
 *   оно уже занятие и живёт в карточке занятия;
 * - **окно преподавателя**: сама карточка и есть подтверждение записи. Время в
 *   зоне ученика и строка про отмену стоят ДО кнопки, а не после нажатия.
 *
 * ⚠️ **Отказ записи не перечитывает неделю сразу.** Окно после `409` из сетки
 * пропадает, и вместе с его коробкой пропал бы якорь карточки: поповер уехал
 * бы в угол экрана вместе с объяснением, почему записаться не вышло. Поэтому
 * отказ показывается здесь, а неделя перечитывается при закрытии карточки
 * (`onStale`).
 */
export function OpeningCard({
  opening,
  zone,
  onChanged,
  onStale,
  onClose,
}: {
  opening: GridOpening;
  /** Зона СМОТРЯЩЕГО: время записи ученику называется в его часах. */
  zone: string;
  /** Запись состоялась либо окно закрыто: перечитать неделю сейчас. */
  onChanged: () => Promise<void>;
  /** Окно, скорее всего, уже не то: перечитать неделю, когда карточку закроют. */
  onStale: () => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Отказ получен: кнопка больше ничего не сделает, и её нет. */
  const [refused, setRefused] = useState(false);

  const when = `${dayTitle(localDateAt(opening.at, zone))}, ${timeText(opening.at, zone)}`;

  const act = () =>
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        if (opening.mine) await api.closeOpening(opening.id);
        else await api.bookOpening(opening.id);
        await onChanged();
      } catch (e) {
        setErr(errorText(e));
        if (e instanceof api.ApiError && e.status === 409) {
          setRefused(true);
          onStale();
        }
      } finally {
        setBusy(false);
      }
    })();

  return (
    <div className="card wk">
      <div className="wk__row">
        <span className="wk__time">{timeText(opening.at, zone)}</span>
        <div className="wk__main">
          <div className="card__title">
            {opening.mine ? "Открыто для записи" : "Свободное время"}
          </div>
          <p className="muted">
            {when} · {String(opening.durationMin)} мин
            {!opening.mine && ` · ${opening.teacherName}`}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--ico btn--quiet wk__close"
          onClick={onClose}
          title="Закрыть карточку"
          aria-label="Закрыть карточку"
        >
          <CloseIcon />
        </button>
      </div>

      {opening.mine ? (
        opening.covered && (
          /*
            Окно не гаснет само: занятие поверх него поставили позже, и сервер
            закроет окно на первой же попытке записи (1.4 проекта). Сказано это
            тут, потому что в коробке сетки на это место есть только значок
          */
          <p className="note">
            В это время у вас уже стоит занятие. Как только кто-то из учеников попробует
            записаться, время закроется для записи.
          </p>
        )
      ) : (
        <div className="note">
          <p>Время в зоне {zoneLabel(zone)}.</p>
          <p>Отменить запись можно до начала занятия.</p>
        </div>
      )}

      {err !== null && <p className="err">{err}</p>}

      {!refused && (
        <div className="wk__tools">
          <button
            type="button"
            className={opening.mine ? "btn" : "btn btn--primary"}
            disabled={busy}
            onClick={act}
          >
            {busy ? "…" : opening.mine ? "Закрыть запись" : "Записаться"}
          </button>
        </div>
      )}
    </div>
  );
}
